import { FileTypes, SpiderService } from '../services/scraper';
import type { WithPaginationResult } from '../hooks';
import type {
  CompanyInfo,
  PublicParsePayload,
  TasksDetails,
  TaskStatus,
} from '../services/scraper';
import type {
  PaginationInfo,
  TaskDetails,
  TaskResponse,
  TasksResponse,
  TaskStatusResponse,
} from '../services/scraper';
import { getCurrentOrg } from './getCurrentOrg';

type TaskData = TaskDetails;
type TaskDetailsData = TaskData & {
  screenshot?: string;
  id?: string;
};

export type TasksDetailsResponse = {
  tasks: Array<TaskDetailsData>;
  pagination: PaginationInfo;
  status_metrics?: {
    in_process?: number | null;
    success?: number | null;
    failure?: number | null;
  };
};
export type TaskStatusKey = keyof typeof TaskStatus;
export type TaskStatusValue = (typeof TaskStatus)[TaskStatusKey];
export type TaskPayload = PublicParsePayload;
export type TaskResult = TaskResponse | TasksResponse | TaskStatusResponse;
export type CompanyData = CompanyInfo;
export type TasksDetailsResult = TasksDetails;

export const getScrapingList = async (
  page: number,
  limit: number,
  url?: string | null,
  campaing_name?: string | null,
  status?: TaskStatusValue | null
): Promise<WithPaginationResult<TasksDetailsResponse>> => {
  const response = await SpiderService.getTasksScraperApiV1SpiderTasksGet(
    getCurrentOrg(),
    page,
    limit,
    url,
    campaing_name,
    status
  );

  return {
    content: [
      {
        tasks: response.tasks || [],
        pagination: response.pagination,
        status_metrics: (response as any).status_metrics,
      },
    ],
  };
};

export const addScrapingTask = (payload: TaskPayload): Promise<TaskResult> => {
  return SpiderService.publicParseScraperApiV1SpiderPublicParsePost(
    {
      ...payload,
      organization_id: getCurrentOrg(),
    },
    getCurrentOrg()
  );
};

export const retryScrapingTask = (taskId: string): Promise<TaskResult> => {
  return SpiderService.retryTaskScraperApiV1SpiderTasksTaskIdRetryPost(
    taskId,
    getCurrentOrg()
  );
};

export const cancelScrapingTask = (taskId: string): Promise<void> => {
  return SpiderService.cancelTaskScraperApiV1SpiderTasksTaskIdCancelDelete(
    taskId,
    getCurrentOrg()
  );
};

export const bulkScrapingTasksExport = (
  taskIds: string[] | null,
  url?: string | null,
  campaignName?: string | null
): Promise<TasksDetailsResult> => {
  const taskIdsToPass = taskIds === null ? [] : taskIds;
  return SpiderService.exportTasksScraperApiV1SpiderTasksExportGet(
    getCurrentOrg(),
    taskIdsToPass,
    url,
    campaignName
  );
};

//TODO: temporary solution until OpenAPI is fixed by provider
export const downloadScrapingTask = async (
  taskId: string,
  fileType: FileTypes = FileTypes.JSON
): Promise<Blob> => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_API_URL}/scraper/api/v1/spider/tasks/${taskId}/result/download/?file_type=${fileType}`,
    {
      method: 'GET',
      headers: {
        Accept:
          fileType === FileTypes.JSON ? 'application/json' : 'application/zip',
        'x-ai-agent-organization-context': getCurrentOrg(),
      },
      redirect: 'follow',
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Download failed: ${response.status} - ${errorText}`);
  }

  return response.blob();
};

export const deleteScrapingTasks = async (taskIds: string[]): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_API_URL}/scraper/api/v1/spider/tasks/remove/`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-ai-agent-organization-context': getCurrentOrg(),
      },
      body: JSON.stringify({ ids: taskIds }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Delete failed: ${response.status} - ${errorText}`);
  }
};
